// Dependencies
import React, { FC, useState, useRef, useEffect, ChangeEvent, DragEvent } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  CircularProgress,
  LinearProgress,
  Chip,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

// Styles
import styles from '../styles';

// APIs
import * as portfolioUploadApi from '../../../apis/portfolio-upload.apis';

// Context
import { useToastContext } from '../../../components/Toast/toastContext';
import { ROUTES } from '../../../contants';
import { PortfolioUploadTabs } from '../components/PortfolioUploadTabs';

interface FileUploadItem {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  result?: any;
  error?: string;
}

// Portfolio Upload Add Page
export const PortfolioUploadAddPage: FC = () => {
  const navigate = useNavigate();
  const { activeToast } = useToastContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [uploading, setUploading] = useState(false);
  const [uploadedData, setUploadedData] = useState<any>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileQueue, setFileQueue] = useState<FileUploadItem[]>([]);

  const validateFile = (file: File): boolean => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.ms-excel.sheet.macroEnabled.12',
    ];

    const validExtensions = ['.xlsx', '.xls', '.xlsm'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    return validTypes.includes(file.type) || validExtensions.includes(fileExtension);
  };

  const uploadSingleFile = async (fileItem: FileUploadItem) => {
    const { file, id } = fileItem;
    
    // Update status to uploading
    setFileQueue(prev => prev.map(item => 
      item.id === id ? { ...item, status: 'uploading', progress: 0 } : item
    ));

    try {
      const response = await portfolioUploadApi.uploadPortfolio(file);

      if (response.success) {
        if (response.updatedExisting) {
          activeToast({
            type: 'success',
            text: 'Existing ISIN group found. Previous data was replaced with this upload.',
          });
        }

        // Update status to success
        setFileQueue(prev => prev.map(item => 
          item.id === id 
            ? { ...item, status: 'success', progress: 100, result: response.data }
            : item
        ));

        // If this is the first file, set it as the preview data
        if (!uploadedData) {
          setUploadedData(response.data);
        }
      }
    } catch (error: any) {
      // Update status to error
      setFileQueue(prev => prev.map(item => 
        item.id === id 
          ? { ...item, status: 'error', progress: 0, error: error.msg || 'Failed to upload' }
          : item
      ));
    }
  };

  const processFileQueue = async () => {
    const pendingFiles = fileQueue.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setUploading(true);
    
    // Upload files sequentially to avoid overwhelming the server
    for (const fileItem of pendingFiles) {
      await uploadSingleFile(fileItem);
    }

    setUploading(false);
    
    // Show summary
    const successCount = fileQueue.filter(f => f.status === 'success').length;
    const errorCount = fileQueue.filter(f => f.status === 'error').length;
    
    if (successCount > 0) {
      activeToast({
        type: 'success',
        text: `Successfully uploaded ${successCount} file(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
      });
    }
    
    if (errorCount > 0 && successCount === 0) {
      activeToast({
        type: 'error',
        text: `Failed to upload ${errorCount} file(s)`,
      });
    }
  };

  const addFilesToQueue = (files: FileList | File[] | null | undefined) => {
    if (!files) return;
    
    // Convert FileList to array, or use array directly
    const fileArray = Array.isArray(files) ? files : Array.from(files);
    if (!Array.isArray(fileArray) || fileArray.length === 0) return;
    
    const validFiles: FileUploadItem[] = [];
    const invalidFiles: string[] = [];

    fileArray.forEach((file) => {
      if (file && file instanceof File) {
        if (validateFile(file)) {
          validFiles.push({
            id: `${Date.now()}-${Math.random()}`,
            file,
            status: 'pending',
            progress: 0,
          });
        } else {
          invalidFiles.push(file.name);
        }
      }
    });

    if (invalidFiles.length > 0) {
      activeToast({
        type: 'error',
        text: `Invalid file(s): ${invalidFiles.join(', ')}. Please upload Excel files only.`,
      });
    }

    if (validFiles.length > 0) {
      setFileQueue(prev => [...prev, ...validFiles]);
    }
  };

  const removeFileFromQueue = (id: string) => {
    setFileQueue(prev => prev.filter(item => item.id !== id));
  };

  const validateAndUploadFile = async (file: File) => {
    if (!file) return;

    if (!validateFile(file)) {
      activeToast({
        type: 'error',
        text: 'Please upload a valid Excel file (.xlsx, .xls, .xlsm)',
      });
      return;
    }

    // For single file mode, add to queue and upload immediately
    addFilesToQueue([file]);
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      addFilesToQueue(files);
    }
    // Reset input so same files can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (uploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      addFilesToQueue(files);
    }
  };

  // Auto-upload when files are added to queue
  useEffect(() => {
    const pendingFiles = fileQueue.filter(f => f.status === 'pending');
    if (pendingFiles.length > 0 && !uploading) {
      processFileQueue();
    }
  }, [fileQueue.length, uploading]);

  const handleSave = async () => {
    // For multi-file upload, we don't need to save - files are already uploaded
    // Just navigate back to the list
    const successCount = fileQueue.filter(f => f.status === 'success').length;
    
    if (successCount === 0) {
      activeToast({
        type: 'error',
        text: 'Please upload at least one Excel file',
      });
      return;
    }

    activeToast({
      type: 'success',
      text: `Successfully processed ${successCount} file(s)!`,
    });
    navigate(ROUTES.PORTFOLIO_UPLOAD);
  };

  return (
    <Box sx={styles.container}>
      <Stack spacing={24}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="h4">Add Portfolio Data from Excel File</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTES.PORTFOLIO_UPLOAD)}
          >
            Back
          </Button>
        </Stack>

        {/* Upload Section - name = filename, ISINs parsed from config sheet (row 2, B2+) */}
        <Paper
          sx={{
            ...styles.uploadSection,
            ...(isDragging ? styles.dragOver : {}),
            cursor: uploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Stack spacing={2} alignItems="center">
            <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h6">
              Upload Portfolio Data
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {isDragging
                ? 'Drop your Excel files here'
                : 'Drag and drop Excel files here, or click to browse. Name is taken from the filename; ISINs are read from the config sheet (3rd tab, row 2 from B2).'}
            </Typography>
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              onClick={handleUploadClick}
              disabled={uploading}
              sx={styles.uploadButton}
            >
              {uploading ? 'Uploading...' : 'Browse Files'}
            </Button>
            
            {fileQueue.length > 0 && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Upload Queue ({fileQueue.length} file{fileQueue.length > 1 ? 's' : ''})
                </Typography>
                <Stack spacing={1}>
                  {fileQueue.map((fileItem) => (
                    <Paper key={fileItem.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 'medium' }}>
                            {fileItem.file.name}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            {fileItem.status === 'uploading' && (
                              <LinearProgress variant="indeterminate" />
                            )}
                            {fileItem.status === 'success' && (
                              <Typography variant="caption" color="success.main">
                                Uploaded successfully
                              </Typography>
                            )}
                            {fileItem.status === 'error' && (
                              <Typography variant="caption" color="error.main">
                                {fileItem.error || 'Upload failed'}
                              </Typography>
                            )}
                            {fileItem.status === 'pending' && (
                              <Typography variant="caption" color="text.secondary">
                                Waiting to upload...
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box>
                          {fileItem.status === 'success' && (
                            <CheckCircleIcon color="success" />
                          )}
                          {fileItem.status === 'error' && (
                            <ErrorIcon color="error" />
                          )}
                          {fileItem.status === 'pending' && (
                            <CircularProgress size={20} />
                          )}
                          {fileItem.status === 'uploading' && (
                            <CircularProgress size={20} />
                          )}
                        </Box>
                        {(fileItem.status === 'pending' || fileItem.status === 'error') && (
                          <IconButton
                            size="small"
                            onClick={() => removeFileFromQueue(fileItem.id)}
                            disabled={uploading && fileItem.status === 'pending'}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Data Display Section */}
        {uploadedData && (
          <Box sx={styles.dataSection}>
            <PortfolioUploadTabs uploadedData={uploadedData} />
          </Box>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={8} justifyContent="flex-end">
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate(ROUTES.PORTFOLIO_UPLOAD)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={fileQueue.filter(f => f.status === 'success').length === 0}
          >
            {(() => {
              const successCount = fileQueue.filter(f => f.status === 'success').length;
              return successCount > 0 
                ? `Save (${successCount} file${successCount > 1 ? 's' : ''})`
                : 'Save';
            })()}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
